import type { Vec2 } from '../geo/types';
import { itemAt } from './tourPath';

/** A cleared loop lets the visitor turn at a chapel instead of reversing their walking gait. */
export function roundReversals(
  points: readonly Vec2[],
  clear: (a: Vec2, b: Vec2) => boolean
): readonly Vec2[] {
  return points.flatMap((p, i): Vec2[] => {
    const a = itemAt(points, (i + points.length - 1) % points.length);
    const b = itemAt(points, (i + 1) % points.length);
    const incoming = Math.hypot(p[0] - a[0], p[1] - a[1]);
    const outgoing = Math.hypot(b[0] - p[0], b[1] - p[1]);
    const dx = (p[0] - a[0]) / incoming,
      dy = (p[1] - a[1]) / incoming;
    if ((dx * (b[0] - p[0]) + dy * (b[1] - p[1])) / outgoing > -0.9999) return [p];
    for (const radius of [0.8, 0.65, 0.5, 0.4, 0.3, 0.2]) {
      if (radius * 3 >= Math.min(incoming, outgoing)) continue;
      for (const side of [1, -1]) {
        const loop = turnaround(p, dx, dy, radius, side);
        if (
          loop.every((q, k) => clear(k ? itemAt(loop, k - 1) : a, q)) &&
          clear(itemAt(loop, loop.length - 1), b)
        )
          return loop;
      }
    }
    throw new Error(`Church tour: no cleared turnaround at ${p.join(', ')}`);
  });
}

function turnaround(p: Vec2, dx: number, dy: number, radius: number, side: number): Vec2[] {
  const at = (u: number, v: number): Vec2 => [
    p[0] + dx * u - dy * v * side,
    p[1] + dy * u + dx * v * side,
  ];
  const loop: Vec2[] = [];
  const curve = (controls: readonly Vec2[]) => {
    for (let k = 0; k <= 20; k++) {
      if (k === 0 && loop.length) continue;
      const t = k / 20,
        u = 1 - t;
      const weights = [u * u * u, 3 * u * u * t, 3 * u * t * t, t * t * t];
      loop.push(
        at(
          controls.reduce((sum, c, j) => sum + c[0] * itemAt(weights, j), 0),
          controls.reduce((sum, c, j) => sum + c[1] * itemAt(weights, j), 0)
        )
      );
    }
  };
  curve([
    [-3 * radius, 0],
    [-2.3 * radius, 0],
    [-1.7 * radius, radius],
    [-radius, radius],
  ]);
  for (let k = 1; k <= 40; k++) {
    const angle = (k * Math.PI) / 40;
    loop.push(at(-radius + Math.sin(angle) * radius, Math.cos(angle) * radius));
  }
  curve([
    [-radius, -radius],
    [-1.7 * radius, -radius],
    [-2.3 * radius, 0],
    [-3 * radius, 0],
  ]);
  return loop;
}
